import { pickPartForCloud, shrinkPartsForCloud } from "@/cloud/cloud-part-image-fit";
import type { DungeonFloorPublishSnapshot } from "@/dungeon/dungeon-publish";
import { DUNGEON_PUBLISH_MAX_BYTES } from "@/dungeon/dungeon-publish-validate";
import { normalizeHomeRoom, type HomePartDefinition, type HomeRoomData } from "@/home/storage";

export type DungeonPublishCloudBody = {
  floors: DungeonFloorPublishSnapshot[];
  homeRoom: HomeRoomData;
};

function publishBodySize(body: DungeonPublishCloudBody): number {
  return new TextEncoder().encode(JSON.stringify(body)).length;
}

function collectPartsById(body: DungeonPublishCloudBody): Map<string, HomePartDefinition> {
  const byId = new Map<string, HomePartDefinition>();
  for (const floor of body.floors) {
    for (const part of [...floor.sceneParts, ...floor.monsterParts]) {
      if (!byId.has(part.id)) byId.set(part.id, part);
    }
  }
  for (const part of body.homeRoom.parts) {
    if (!byId.has(part.id)) byId.set(part.id, part);
  }
  return byId;
}

function applyPartMap(
  body: DungeonPublishCloudBody,
  byId: Map<string, HomePartDefinition>,
): DungeonPublishCloudBody {
  const part = (id: string) => byId.get(id)!;
  return {
    homeRoom: normalizeHomeRoom({
      ...body.homeRoom,
      parts: body.homeRoom.parts.map((p) => part(p.id)),
    }),
    floors: body.floors.map((floor) => ({
      ...floor,
      sceneParts: floor.sceneParts.map((p) => part(p.id)),
      monsterParts: floor.monsterParts.map((p) => part(p.id)),
    })),
  };
}

function slimParts(body: DungeonPublishCloudBody): DungeonPublishCloudBody {
  const byId = collectPartsById(body);
  for (const [id, part] of byId) {
    byId.set(id, pickPartForCloud(part));
  }
  return applyPartMap(body, byId);
}

async function shrinkAllParts(
  body: DungeonPublishCloudBody,
  maxEdge: number,
): Promise<DungeonPublishCloudBody> {
  const byId = collectPartsById(body);
  const unique = [...byId.values()];
  const shrunk = await shrinkPartsForCloud(unique, maxEdge);
  const next = new Map<string, HomePartDefinition>();
  for (let i = 0; i < unique.length; i += 1) {
    next.set(unique[i]!.id, shrunk[i]!);
  }
  return applyPartMap(body, next);
}

export type FitDungeonPublishResult = {
  body: DungeonPublishCloudBody;
  imageCompressed: boolean;
};

/**
 * Slim + compress images for Firestore manifest only (local assets unchanged).
 */
export async function fitDungeonPublishPayloadForCloud(
  body: DungeonPublishCloudBody,
): Promise<FitDungeonPublishResult> {
  const initialSize = publishBodySize(body);
  let working = slimParts(body);
  if (publishBodySize(working) <= DUNGEON_PUBLISH_MAX_BYTES) {
    return { body: working, imageCompressed: initialSize > publishBodySize(working) };
  }

  let maxEdge = 640;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    working = await shrinkAllParts(working, maxEdge);
    if (publishBodySize(working) <= DUNGEON_PUBLISH_MAX_BYTES) {
      return { body: working, imageCompressed: true };
    }
    maxEdge = Math.round(maxEdge * 0.72);
  }

  const kb = Math.round(publishBodySize(working) / 1024);
  throw new Error(
    `ข้อมูลด่านใหญ่เกินไป (${kb} KB) หลังย่ออัตโนมัติ — ลดรูป/ชั้นหรือชิ้นฉากก่อนเผยแพร่`,
  );
}
